import { useState } from "react";
import { Collapse, Input, Button, Tooltip, Popover, Typography } from "antd";
import {
  DeleteOutlined,
  TableOutlined,
  DatabaseOutlined,
  CommentOutlined,
} from "@ant-design/icons";
import { useDiagram, useUndoRedo } from "../../../hooks";
import { Action, ObjectType, defaultBlue } from "../../../data/constants";
import ColorPalette from "../../ColorPicker";
import TableField from "./TableField";
import IndexDetails from "./IndexDetails";
import { useTranslation } from "react-i18next";
import { dbToTypes } from "../../../data/datatypes";
import { useDispatch, useSelector } from "react-redux";
import { getTenantConfig } from "ee/selectors/tenantSelectors";
import styled from "styled-components";

const IconContainer = styled.div<{ primaryColor: string }>`
  width: 18px;
  height: 18px;
  min-width: 18px;
  min-height: 18px;
  flex-shrink: 0;
  border-radius: 50%;

  border: 2px solid
    ${({ primaryColor }) =>
      primaryColor.includes("gradient") ? "transparent" : primaryColor};
  ${({ primaryColor }) =>
    primaryColor.includes("gradient") &&
    `background:
      linear-gradient(white, white) padding-box,
        ${primaryColor}
      } border-box;
    background-origin: padding-box, border-box;
    background-clip: padding-box, border-box;
    `}

  display: flex;
  justify-content: center;
  align-items: center;
`;

const AddIcon = styled.div<{ primaryColor: string }>`
  font-size: 14px;
  font-weight: 400;
  color: ${({ primaryColor }) =>
    primaryColor.includes("gradient") ? "#2dd4bf" : primaryColor};
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const { Paragraph } = Typography;

export default function TableInfo({ data }) {
  const { t } = useTranslation();
  const [indexActiveKey, setIndexActiveKey] = useState("");
  const { deleteTable, updateTable, updateField, setRelationships, database } =
    useDiagram();
  const { setUndoStack, setRedoStack } = useUndoRedo();
  const [editField, setEditField] = useState({});
  const [drag, setDrag] = useState({
    draggingElementIndex: null,
    draggingOverIndexList: [],
  });
  const tenantConfig = useSelector(getTenantConfig);
  const { brandColors } = tenantConfig;

  return (
    <div>
      {/* <div className="flex items-center mb-2.5 ml-[42px]">
        <div className="text-md font-semibold break-keep w-[42px]">
          {t("name")}:{" "}
        </div>
        <Paragraph
          editable={{
            onChange: (value) => {
              updateTable(data.id, { name: value });
              setUndoStack((prev) => [
                ...prev,
                {
                  action: Action.EDIT,
                  element: ObjectType.TABLE,
                  component: "self",
                  tid: data.id,
                  undo: editField,
                  redo: { name: value },
                  message: t("edit_table", {
                    tableName: value,
                    extra: "[name]",
                  }),
                },
              ]);
              setRedoStack([]);
            },
            onStart: () => setEditField({ name: data.name }),
          }}
          className="ms-2"
          style={{ 
            color: data.name.trim() === "" ? "#ff4d4f" : "inherit",
            margin: 0 
          }}
        >
          {data.name || t("name")}
        </Paragraph>
      </div> */}
      <div className="mt-3 mb-3">
        <Collapse
          // activeKey={indexActiveKey}
          // onChange={(itemKey) => setIndexActiveKey(itemKey)}
          accordion
          items={[
            {
              key: "fields",
              label: (
                <div className="flex items-center justify-between">
                  <span>字段</span>
                  <Tooltip title={"添加字段"} placement="bottom" trigger="hover" mouseLeaveDelay={0.1}>
                    <IconContainer
                      primaryColor={brandColors.primary}
                      onClick={(e) => {
                        e.stopPropagation();
                        setUndoStack((prev) => [
                          ...prev,
                          {
                            action: Action.EDIT,
                            element: ObjectType.TABLE,
                            component: "field_add",
                            tid: data.id,
                            message: t("edit_table", {
                              tableName: data.name,
                              extra: "[add field]",
                            }),
                          },
                        ]);
                        setRedoStack([]);
                        updateTable(data.id, {
                          fields: [
                            ...data.fields,
                            {
                              name: "",
                              type: "",
                              default: "",
                              check: "",
                              primary: false,
                              unique: false,
                              notNull: false,
                              increment: false,
                              comment: "",
                              id: data.fields.length,
                            },
                          ],
                        });
                      }}
                    >
                      <AddIcon primaryColor={brandColors.primary}>+</AddIcon>
                    </IconContainer>
                  </Tooltip>
                </div>
              ),
              children: data.fields.map((f, j) => (
                <div
                  key={"field_" + j}
                  className={`cursor-pointer ${drag.draggingOverIndexList.includes(j) ? "opacity-25" : ""}`}
                  style={{ direction: "ltr" }}
                  draggable
                  onDragStart={() => {
                    setDrag((prev) => ({ ...prev, draggingElementIndex: j }));
                  }}
                  onDragLeave={() => {
                    setDrag((prev) => ({
                      ...prev,
                      draggingOverIndexList: prev.draggingOverIndexList.filter(
                        (index) => index !== j,
                      ),
                    }));
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    if (drag.draggingElementIndex != null) {
                      if (j !== drag.draggingElementIndex) {
                        setDrag((prev) => {
                          if (prev.draggingOverIndexList.includes(j)) {
                            return prev;
                          }

                          return {
                            ...prev,
                            draggingOverIndexList:
                              prev.draggingOverIndexList.concat(j),
                          };
                        });
                      }

                      return;
                    }
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    const index = drag.draggingElementIndex;
                    setDrag({
                      draggingElementIndex: null,
                      draggingOverIndexList: [],
                    });
                    if (index == null || index === j) {
                      return;
                    }

                    const a = data.fields[index];
                    const b = data.fields[j];

                    updateField(data.id, index, {
                      ...b,
                      ...(!dbToTypes[database][b.type].isSized && { size: "" }),
                      ...(!dbToTypes[database][b.type].hasCheck && {
                        check: "",
                      }),
                      ...(dbToTypes[database][b.type].noDefault && {
                        default: "",
                      }),
                      id: index,
                    });
                    updateField(data.id, j, {
                      ...a,
                      ...(!dbToTypes[database][a.type].isSized && { size: "" }),
                      ...(!dbToTypes[database][a.type].hasCheck && {
                        check: "",
                      }),
                      ...(!dbToTypes[database][a.type].noDefault && {
                        default: "",
                      }),
                      id: j,
                    });

                    setRelationships((prev) =>
                      prev.map((e) => {
                        if (e.startTableId === data.id) {
                          if (e.startFieldId === index) {
                            return { ...e, startFieldId: j };
                          }
                          if (e.startFieldId === j) {
                            return { ...e, startFieldId: index };
                          }
                        }
                        if (e.endTableId === data.id) {
                          if (e.endFieldId === index) {
                            return { ...e, endFieldId: j };
                          }
                          if (e.endFieldId === j) {
                            return { ...e, endFieldId: index };
                          }
                        }
                        return e;
                      }),
                    );
                  }}
                  onDragEnd={(e) => {
                    e.preventDefault();
                    setDrag({
                      draggingElementIndex: null,
                      draggingOverIndexList: [],
                    });
                  }}
                >
                  <TableField data={f} tid={data.id} index={j} />
                </div>
              )),
            },
          ]}
        />
      </div>

      {/* {data.indices.length > 0 && ( */}
      <div className="mt-3 mb-3">
        <Collapse
          // activeKey={indexActiveKey}
          // onChange={(itemKey) => setIndexActiveKey(itemKey)}
          accordion
          items={[
            {
              key: "indices",
              label: (
                <div className="flex items-center justify-between">
                  <span>{t("indices")}</span>
                  <Tooltip title={"添加索引"} placement="bottom" trigger="hover" mouseLeaveDelay={0.1}>
                    <IconContainer
                      primaryColor={brandColors.primary}
                      onClick={(e) => {
                        e.stopPropagation();
                        setIndexActiveKey("indices");
                        setUndoStack((prev) => [
                          ...prev,
                          {
                            action: Action.EDIT,
                            element: ObjectType.TABLE,
                            component: "index_add",
                            tid: data.id,
                            message: t("edit_table", {
                              tableName: data.name,
                              extra: "[add index]",
                            }),
                          },
                        ]);
                        setRedoStack([]);
                        updateTable(data.id, {
                          indices: [
                            ...data.indices,
                            {
                              id: data.indices.length,
                              name: `${data.name}_index_${data.indices.length}`,
                              unique: false,
                              fields: [],
                            },
                          ],
                        });
                      }}
                    >
                      <AddIcon primaryColor={brandColors.primary}>+</AddIcon>
                    </IconContainer>
                  </Tooltip>
                </div>
              ),
              children: data.indices.map((idx, k) => (
                <IndexDetails
                  key={"index_" + k}
                  data={idx}
                  iid={k}
                  tid={data.id}
                  fields={data.fields.map((e) => ({
                    value: e.name,
                    label: e.name,
                  }))}
                />
              )),
            },
          ]}
        />
      </div>
      {/* // )} */}
      <div className="mt-3 mb-3">
        <Collapse
          items={[
            {
              key: "comment",
              label: (
                <div className="flex items-center justify-between">
                  <span>{t("comment")}</span>
                  {/* <CommentOutlined /> */}
                </div>
              ),
              children: (
                <Input.TextArea
                  value={data.comment}
                  autoSize
                  placeholder={t("comment")}
                  rows={4}
                  onChange={(e) =>
                    updateTable(data.id, { comment: e.target.value }, false)
                  }
                  onFocus={(e) => setEditField({ comment: e.target.value })}
                  onBlur={(e) => {
                    if (e.target.value === editField.comment) return;
                    setUndoStack((prev) => [
                      ...prev,
                      {
                        action: Action.EDIT,
                        element: ObjectType.TABLE,
                        component: "self",
                        tid: data.id,
                        undo: editField,
                        redo: { comment: e.target.value },
                        message: t("edit_table", {
                          tableName: e.target.value,
                          extra: "[comment]",
                        }),
                      },
                    ]);
                    setRedoStack([]);
                  }}
                />
              ),
            },
          ]}
        />
      </div>
      {/* <div className="flex justify-end items-center gap-1 mb-2">
        <div className="flex gap-1">
          <div>
            <Popover
              content={
                <div className="popover-theme">
                  <ColorPalette
                    currentColor={data.color}
                    onClearColor={() => {
                      setUndoStack((prev) => [
                        ...prev,
                        {
                          action: Action.EDIT,
                          element: ObjectType.TABLE,
                          component: "self",
                          tid: data.id,
                          undo: { color: data.color },
                          redo: { color: defaultBlue },
                          message: t("edit_table", {
                            tableName: data.name,
                            extra: "[color]",
                          }),
                        },
                      ]);
                      setRedoStack([]);
                      updateTable(data.id, { color: defaultBlue });
                    }}
                    onPickColor={(c) => {
                      setUndoStack((prev) => [
                        ...prev,
                        {
                          action: Action.EDIT,
                          element: ObjectType.TABLE,
                          component: "self",
                          tid: data.id,
                          undo: { color: data.color },
                          redo: { color: c },
                          message: t("edit_table", {
                            tableName: data.name,
                            extra: "[color]",
                          }),
                        },
                      ]);
                      setRedoStack([]);
                      updateTable(data.id, { color: c });
                    }}
                  />
                </div>
              }
              trigger="click"
              placement="bottomLeft"
              arrow
            >
              <div
                className="h-[32px] w-[32px] rounded"
                style={{ backgroundColor: data.color }}
              />
            </Popover>
          </div>
          <Tooltip title={"删除表"} placement="top">
            <Button
              icon={<DeleteOutlined />}
              danger
              onClick={() => deleteTable(data.id)}
              style={{ width: "40px" }}
            />
          </Tooltip>
        </div>
      </div> */}
    </div>
  );
}
